import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Send, Sparkles, Trash2, BookOpen, Volume2, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, getToken, BRANCH_COLORS, TOOL_ROUTES } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { t, getLang } from "@/lib/i18n";

const Avatar = ({ role }) => (
  <span className="h-8 w-8 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 border" style={{ background: `${BRANCH_COLORS[role.id]}22`, color: BRANCH_COLORS[role.id], borderColor: BRANCH_COLORS[role.id] }}>{role.initials}</span>
);

const Rich = ({ text }) => text.split(/(\*\*[^*]+\*\*)/g).map((part, i) => part.startsWith("**") && part.endsWith("**") ? <strong key={i} className="text-foreground">{part.slice(2, -2)}</strong> : part);

let currentAudio = null;
const SpeakButton = ({ text, roleId, testId }) => {
  const [state, setState] = useState("idle");
  const toggle = async () => {
    if (state === "playing") { currentAudio?.pause(); currentAudio = null; setState("idle"); return; }
    setState("loading");
    try {
      const r = await api.post("/tts", { text, role_id: roleId });
      currentAudio?.pause();
      const audio = new Audio(`${process.env.REACT_APP_BACKEND_URL}${r.data.url}`);
      currentAudio = audio;
      audio.onended = () => setState("idle");
      audio.onerror = () => { setState("idle"); toast.error("Riproduzione non riuscita"); };
      await audio.play(); setState("playing");
    } catch (e) { setState("idle"); toast.error(e.response?.data?.detail || t("Voce non disponibile")); }
  };
  return (
    <button data-testid={testId} onClick={toggle} disabled={!text || state === "loading"} title={t("Ascolta")} className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border transition-colors ${state === "playing" ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:text-primary hover:border-primary"} disabled:opacity-50`}>
      {state === "loading" ? <Loader2 size={11} className="animate-spin" /> : state === "playing" ? <Square size={10} /> : <Volume2 size={11} />} {state === "loading" ? t("preparo la voce…") : state === "playing" ? "stop" : t("ascolta")}
    </button>
  );
};

export const AiTeamPanel = ({ roles, roleId }) => {
  const byId = Object.fromEntries(roles.map((r) => [r.id, r]));
  const [q, setQ] = useState("");
  const [turns, setTurns] = useState([]);
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const listRef = useRef(null);

  useEffect(() => { api.get("/team/ai/conversations").then((r) => { const c = r.data[0]; if (c) { setSessionId(c.session_id); setTurns(c.turns || []); } }); }, []);
  useEffect(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, [turns]);

  const ask = async (text) => {
    const question = (text || q).trim();
    if (question.length < 3 || busy) return;
    setBusy(true); setQ("");
    const turn = { question, roles: [], answers: [], sources: [], live: true };
    setTurns((x) => [...x, turn]);
    const update = (fn) => setTurns((x) => { const c = [...x]; c[c.length - 1] = fn({ ...c[c.length - 1] }); return c; });
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/team/ai/ask`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` }, body: JSON.stringify({ question, role_id: roleId || null, session_id: sessionId, lang: getLang() }) });
      if (!res.ok) throw new Error("HTTP " + res.status);
      const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = "";
      while (true) {
        const { value, done } = await reader.read(); if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const ev = JSON.parse(p.slice(6));
          if (ev.type === "start") { setSessionId(ev.session_id); update((x) => ({ ...x, roles: ev.roles, sources: ev.sources })); }
          else if (ev.type === "role_start") update((x) => ({ ...x, answers: [...x.answers, { role_id: ev.role_id, text: "" }] }));
          else if (ev.type === "delta") update((x) => { const a = [...x.answers]; const i = a.findIndex((x) => x.role_id === ev.role_id); if (i >= 0) a[i] = { ...a[i], text: a[i].text + ev.text }; return { ...x, answers: a }; });
          else if (ev.type === "done") update((x) => ({ ...x, live: false }));
        }
      }
    } catch (e) { toast.error(t("Il team AI non ha risposto:") + " " + e.message); update((x) => ({ ...x, live: false })); }
    finally { setBusy(false); }
  };

  const clear = () => api.delete("/team/ai/conversations").then(() => { setTurns([]); setSessionId(null); });

  return (
    <section data-testid="ai-team-panel" className="surface p-4 lg:p-5 border-primary/40">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="font-heading font-semibold flex items-center gap-2"><Sparkles size={16} className="text-primary" /> {t("Team AI · GPT 5.4 sul corpus")}</h2>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">{roleId ? `${t("Destinatario")}: ${byId[roleId]?.name}` : t("Chi ha competenza risponde, il Direttore di Metodo sintetizza")}</span>
          {turns.length > 0 && <button data-testid="ai-clear-btn" onClick={clear} className="text-xs text-muted-foreground hover:text-destructive flex items-center gap-1"><Trash2 size={12} /> {t("Nuova sessione")}</button>}
        </div>
      </div>
      <div ref={listRef} className="space-y-4 max-h-[520px] overflow-y-auto pr-1">
        {turns.map((x, i) => (
          <div key={i} data-testid={`ai-turn-${i}`} className="surface-elevated p-4 space-y-3">
            <div className="text-sm font-medium border-l-2 border-primary pl-3">{x.question}</div>
            {x.answers.map((a) => byId[a.role_id] && (
              <div key={a.role_id} className="flex gap-3">
                <Avatar role={byId[a.role_id]} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap"><span className="text-sm font-medium">{byId[a.role_id].person}</span><span className="text-[11px] text-muted-foreground">{byId[a.role_id].name}</span>{!x.live && a.text && <SpeakButton text={a.text} roleId={a.role_id} testId={`ai-speak-${a.role_id}`} />}</div>
                  <p data-testid={`ai-answer-${a.role_id}`} className="text-sm text-foreground/90 mt-0.5 leading-relaxed whitespace-pre-wrap"><Rich text={a.text} />{x.live && a === x.answers[x.answers.length - 1] && <span className="inline-block w-1.5 h-4 bg-primary ml-0.5 animate-pulse align-text-bottom" />}</p>
                  {byId[a.role_id].tools?.[0] && !x.live && <Link to={TOOL_ROUTES[byId[a.role_id].tools[0]]} className="text-[11px] text-primary">{t("Apri strumento →")}</Link>}
                </div>
              </div>
            ))}
            {x.sources?.length > 0 && <div className="flex flex-wrap gap-1.5 pt-1 border-t border-border"><BookOpen size={11} className="text-muted-foreground mt-0.5" />{x.sources.map((s) => <span key={s} className="text-[10px] px-1.5 rounded bg-secondary text-muted-foreground">{s}</span>)}</div>}
          </div>
        ))}
      </div>
      <div className="mt-4">
        <Textarea data-testid="ai-ask-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } }} placeholder={t("Es. Il food cost è al 34%: da dove parto? Quale vino della carta spingo con la tagliata?")} className="bg-background min-h-[70px]" />
        <div className="flex flex-wrap gap-1.5 mt-3">
          {(byId[roleId]?.follow_ups || roles.slice(0, 4).map((r) => r.follow_ups[1])).map((f) => <button key={f} data-testid="ai-followup-chip" disabled={busy} onClick={() => ask(f)} className="text-xs px-2.5 py-1 rounded-full border border-border text-muted-foreground hover:text-primary hover:border-primary disabled:opacity-50">{f}</button>)}
          <Button data-testid="ai-ask-submit" size="sm" className="ml-auto" disabled={busy} onClick={() => ask()}><Send size={14} className="mr-1.5" /> {busy ? t("Il team sta rispondendo…") : t("Chiedi al team AI")}</Button>
        </div>
      </div>
    </section>
  );
};
